import {
  Group,
  Mesh,
  Vector3,
  Color,
  ShaderMaterial,
  DoubleSide,
  Object3D,
  Matrix4,
  type BufferGeometry,
  type Material,
} from 'three'

import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils'

import vertexShader from './_shaders/mona-vert'

import fragmentShader from './_shaders/mona-frag'
import eyeFragmentShader from './_shaders/mona-eye-frag'
import faceFragmentShader from './_shaders/mona-face-frag'

export type CommonUniforms = {
  uProjectionMatrix: {value: Matrix4}
  uViewMatrix: {value: Matrix4}
}

export function disposeObject3D(object: Object3D) {
  const geometries = new Set<BufferGeometry>()
  const materials = new Set<Material>()

  object.traverse(child => {
    if (!(child instanceof Mesh)) return

    geometries.add(child.geometry)
    const childMaterials = Array.isArray(child.material) ? child.material : [child.material]
    for (const material of childMaterials) materials.add(material)
  })

  for (const geometry of geometries) geometry.dispose()
  for (const material of materials) material.dispose()
}

export default class MonaBase {
  group: Group = new Group()
  monaHeadGroup: Group = new Group()
  commonUniforms: CommonUniforms | null
  #sourceGeometries = new Set<BufferGeometry>()

  constructor(commonUniforms: CommonUniforms | null) {
    this.commonUniforms = commonUniforms
    this.group.add(this.monaHeadGroup)
  }

  create(scene: Object3D | Group) {
    const eyeGroup = new Group()
    const sourceMaterials = new Set<Material>()
    eyeGroup.name = 'EyeGroup'
    this.monaHeadGroup.add(eyeGroup)

    scene.traverse(child => {
      if (child.name === 'MonaHead') {
        this.monaHeadGroup.position.copy(child.position)
      }

      if (child instanceof Mesh) {
        const geometry = child.geometry
        const material = this.createMaterial(child.name)
        const childMaterials = Array.isArray(child.material) ? child.material : [child.material]
        for (const sourceMaterial of childMaterials) sourceMaterials.add(sourceMaterial)

        let mesh: Mesh

        switch (child.name) {
          case 'Face':
          case 'Nose':
          case 'Mouth':
          case 'Pupil_Highlight':
            mesh = new Mesh(geometry, material)
            mesh.position.copy(child.position)
            mesh.rotation.copy(child.rotation)
            this.monaHeadGroup.add(mesh)
            break
          case 'Eye':
          case 'Eyelash':
          case 'Pupil':
            mesh = new Mesh(geometry, material)
            mesh.position.copy(child.position)
            mesh.rotation.copy(child.rotation)
            eyeGroup.add(mesh)
            mesh.name = child.name
            break
          default: {
            this.#sourceGeometries.add(geometry)
            const mirroredGeometry = geometry.clone()
            mirroredGeometry.scale(-1, 1, 1)
            const combinedGeometry = mergeBufferGeometries([geometry, mirroredGeometry])
            mirroredGeometry.dispose()

            mesh = new Mesh(combinedGeometry, material)
            mesh.position.copy(child.position)
            mesh.rotation.copy(child.rotation)
            mesh.name = child.name
            if (child.name === 'MonaTorso') {
              this.group.add(mesh)
            } else {
              this.monaHeadGroup.add(mesh)
            }
            break
          }
        }
      }
    })

    for (const material of sourceMaterials) material.dispose()

    const mirroredEyeGroup = eyeGroup.clone()
    mirroredEyeGroup.name = 'EyeGroup_mirrored'
    mirroredEyeGroup.scale.set(-1, 1, 1)
    this.monaHeadGroup.add(mirroredEyeGroup)
  }

  dispose() {
    disposeObject3D(this.group)
    for (const geometry of this.#sourceGeometries) geometry.dispose()
    this.#sourceGeometries.clear()
    this.group.clear()
    this.monaHeadGroup.clear()
  }

  createMaterial(name: string) {
    const defines = {}
    const uniforms = {
      uBaseColor: {value: new Color(0x180e27)}, // black
      uColor1: {value: new Color(0xffffff)}, // white
      uColor2: {value: new Color(0xffffff)}, // white
      uLightDirection: {value: new Vector3(0.0, 2.0, 1.0).normalize()},
      uLightIntensity: {value: name === 'MonaTorso' ? 0.0 : 1.0},
      ...(this.commonUniforms || {}),
    }

    switch (name) {
      case 'Eye':
        uniforms.uBaseColor.value.set(0xdfd4ee) // black
        break
      case 'Eyelash':
        uniforms.uBaseColor.value.set(0x3b224e) // dark purple
        break
      case 'Pupil_Highlight':
        uniforms.uBaseColor.value.set(0xffffff) // white
        break
    }

    switch (name) {
      case 'Pupil':
        uniforms.uBaseColor.value.set(0x080216) // black
        return new ShaderMaterial({
          vertexShader,
          fragmentShader: eyeFragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms: {
            ...uniforms,
            uOutlineColor: {value: new Color(0x28184f)}, // purple
          },
          defines,
        })
      case 'Face':
        return new ShaderMaterial({
          vertexShader,
          fragmentShader: faceFragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms: {
            ...uniforms,
            uColor1: {value: new Color(0xffafc6)}, // skin tone
            uColor2: {value: new Color(0xe99e89)}, // skin tone
            uColor3: {value: new Color(0xe979a1)}, // black
            uColor4: {value: new Color(0xf88580)}, // cheak
          },
          defines,
        })
      default:
        return new ShaderMaterial({
          vertexShader,
          fragmentShader,
          transparent: true,
          side: DoubleSide,
          uniforms,
          defines,
        })
    }
  }

  update() {}
}
